import { TextField, Box, Chip, InputBaseComponentProps } from "@mui/material";
import { forwardRef } from "react";

interface SelectableItem {
    id: number;
    nome: string;
    [key: string]: any;
}

interface MultipleChipsFieldProps<T extends SelectableItem> {
    value: T[] | null;
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    onClick?: () => void;
    formatChipLabel?: (item: T) => string;
}

const ChipInput = forwardRef<HTMLDivElement, { value: SelectableItem[] | null; formatChipLabel?: (item: SelectableItem) => string } & Omit<InputBaseComponentProps, 'onCopy'>>(
    ({ value, formatChipLabel = (item) => `${item.id} - ${item.nome}`, ...props }, ref) => {
        return (
            <Box
                component="div"
                ref={ref}
                {...props}
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflow: 'auto',
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                    '::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    cursor: 'pointer',
                }}
            >
                {value?.map((item) => (
                    <Chip
                        sx={{ mr: 0.5 }}
                        key={item.id}
                        label={formatChipLabel(item)}
                        size="small"
                    />
                ))}
            </Box>
        );
    }
);

export default function MultipleChipsField<T extends SelectableItem>({
    value,
    label = "",
    required = false,
    error = false,
    helperText = "",
    onClick,
    formatChipLabel
}: MultipleChipsFieldProps<T>) {
    const hasValue = value && value.length > 0;
    return (
        <TextField
            required={required}
            error={error}
            label={label}
            variant="outlined"
            fullWidth
            onClick={onClick}
            slotProps={{
                inputLabel: { shrink: hasValue ? true : false },
                input: {
                    inputComponent: ChipInput as any,
                    inputProps: {
                        value,
                        formatChipLabel,
                        sx: {
                            py: 1.5,
                            minHeight: '56px',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            ml: 1,
                            gap: 0.5,
                            '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: '2px'
                                }
                            }
                        }
                    },
                }
            }}
            value={value?.length ? ' ' : ''}
            helperText={error && !helperText ? "Campo obrigatório" : helperText}
            sx={{
                '& .MuiOutlinedInput-root': {
                    minHeight: '56px',
                    alignItems: 'flex-center',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                    }
                },
                '& .MuiInputBase-input': {
                    padding: 0,
                    height: 'auto',
                    minHeight: '24px'
                }
            }}
        />
    );
}